import React from 'react';
import { LockKeyhole } from 'lucide-react';
import { Logo } from '@/shared/icons/logo';
import { cn } from '@/shared/utils/cn';
import { useUserContext } from '@/shared/features/user-context/presentation/hooks/useUserContext';

export interface CheckoutHeaderProps extends React.HTMLAttributes<HTMLElement> {
  logo?: React.ReactNode;
  showLogo?: boolean;
  badgeText?: string;
  badgeIcon?: React.ReactNode;
  rightContent?: React.ReactNode;
  centerContent?: React.ReactNode;
}

export const CheckoutHeader: React.FC<CheckoutHeaderProps> = ({
  logo,
  showLogo = true,
  badgeText = '100% SECURE',
  badgeIcon = <LockKeyhole className="h-5 w-5 text-[#8C8C8C] md:h-6 md:w-6" strokeWidth={2.4}  />,
  rightContent,
  centerContent,
  className,
  ...props
}) => {
  const { userContext } = useUserContext();
  const displayName = (userContext?.customerName || userContext?.userName || '').trim();

  return (
    <header 
      className={cn(
        "relative flex justify-between items-center bg-white px-6 md:px-12 py-6 border-b border-gray-200 sticky top-0 z-50",
        className
      )}
      {...props}
    >
      <div className="flex items-center space-x-2.5">
        {showLogo && (
          logo || (
            <span className="text-base font-bold text-[#011E38] tracking-widest uppercase">
              <Logo showLink={false} className="h-auto w-full max-w-[160px] min-w-[120px]" />
            </span>
          )
        )}
      </div>
      {centerContent && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 md:block">
          {centerContent}
        </div>
      )}
      {rightContent || (
        <div className="flex items-center gap-4 select-none text-[#8C8C8C]">
          {displayName && (
            <>
              <span className="hidden text-lg font-medium md:inline">
                Hi, {displayName}
              </span>
              <span className="hidden h-6 w-px bg-[#A7A7A7] md:inline-block" aria-hidden="true" />
            </>
          )}
          <span className="flex items-center gap-2">
            {badgeIcon}
            <span className="text-xs font-extrabold tracking-[0.1em] md:text-base">
              {badgeText}
            </span>
          </span>
        </div>
      )}
    </header>
  );
};

export default CheckoutHeader;
